import React, { useState } from 'react';
import { isAllowedChatMediaUrl } from './chatMediaUrl';
import { MarkdownContent } from './MarkdownContent';
import type { ChatContentBlock, ChatSource } from './ChatSchemas';

interface ChatContentBlocksProps {
  contentBlocks: ChatContentBlock[];
  sources?: ChatSource[];
  showCitations?: boolean;
}

const getSourceKey = (source: ChatSource, index: number): string =>
  `${source.sourceFilename}-${source.pageNumber ?? 'na'}-${index}`;

const dedupeSources = (sources: ChatSource[]): ChatSource[] => {
  const seen = new Set<string>();
  return sources.filter((source) => {
    const key = `${source.sourceFilename}-${source.pageNumber ?? 'na'}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

const ResponseMediaImage = ({ url, index }: { url: string; index: number }) => {
  const [hasError, setHasError] = useState(false);

  if (!isAllowedChatMediaUrl(url) || hasError) {
    return null;
  }

  return (
    <div className="image-container">
      <img
        src={url}
        alt={`Response media ${index + 1}`}
        className="response-image"
        referrerPolicy="no-referrer"
        onError={() => setHasError(true)}
      />
    </div>
  );
};

export const ChatContentBlocks = ({
  contentBlocks,
  sources = [],
  showCitations = false,
}: ChatContentBlocksProps) => {
  const citationSources = dedupeSources(sources);
  const [citationsExpanded, setCitationsExpanded] = useState(false);

  return (
    <div className="chat-content-blocks">
      {contentBlocks.map((block, blockIndex) => (
        <div key={`content-block-${blockIndex}`} className="content-block">
          {block.text && <MarkdownContent content={block.text} />}

          {block.media && block.media.length > 0 && (
            <div className="content-block-media">
              {block.media.map((mediaItem, mediaIndex) => (
                <ResponseMediaImage
                  key={`media-${blockIndex}-${mediaIndex}`}
                  url={mediaItem.url}
                  index={mediaIndex}
                />
              ))}
            </div>
          )}
        </div>
      ))}

      {showCitations && citationSources.length > 0 && (
        <div className="content-block-citations">
          <button
            type="button"
            className={`citations-toggle ${citationsExpanded ? 'expanded' : ''}`}
            onClick={() => setCitationsExpanded((current) => !current)}
            aria-expanded={citationsExpanded}
          >
            Citations
          </button>
          {citationsExpanded && (
            <ul className="citations-list">
              {citationSources.map((source, sourceIndex) => (
                <li
                  key={getSourceKey(source, sourceIndex)}
                  className="citation-details"
                >
                  <div className="citation-detail-row">
                    <span className="citation-detail-label">
                      source_filename
                    </span>
                    <span className="citation-detail-value">
                      {source.sourceFilename}
                    </span>
                  </div>
                  <div className="citation-detail-row">
                    <span className="citation-detail-label">page_number</span>
                    <span className="citation-detail-value">
                      {source.pageNumber ?? 0}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
